/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { Component, forwardRef } from 'react';

import { Link } from 'react-resource-router';

import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import type { LinkComponentProps } from '@atlassian/search-dialog/search-link-component';

interface SearchResultBoundaryProps {
	children: React.ReactNode;
	onError: () => void;
}

interface SearchResultBoundaryState {
	hasError: boolean;
}

/**
 * Isolates a single search section so a thrown query error (for example a network failure that
 * Relay surfaces as a throw rather than a GraphQL field error) hides only that section and reports
 * an error status, while the sections that already resolved stay visible.
 */
export class SearchResultBoundary extends Component<
	SearchResultBoundaryProps,
	SearchResultBoundaryState
> {
	state: SearchResultBoundaryState = { hasError: false };

	static getDerivedStateFromError(): SearchResultBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(): void {
		this.props.onError();
	}

	render(): React.ReactNode {
		return this.state.hasError ? null : this.props.children;
	}
}

export const ExternalLinkComponent: React.ForwardRefExoticComponent<
	LinkComponentProps & React.RefAttributes<HTMLAnchorElement>
> = forwardRef<HTMLAnchorElement, LinkComponentProps>(({ href = '#', ...props }, ref) => (
	<Anchor
		ref={ref}
		href={href}
		{...props}
		target="_blank"
		xcss={styles.externalLink}
		rel="noopener noreferrer"
	/>
));

export const InternalLinkComponent: React.ForwardRefExoticComponent<
	LinkComponentProps & React.RefAttributes<HTMLAnchorElement>
> = forwardRef<HTMLAnchorElement, LinkComponentProps>(({ href = '#', children, ...props }, ref) => (
	<Link ref={ref} to={href} {...props}>
		{children}
	</Link>
));

export const SearchResultSection = ({
	children,
	heading,
}: {
	children: React.ReactNode;
	heading: string;
}): React.JSX.Element => (
	<>
		<Box xcss={styles.sectionHeading}>
			<Heading as="h3" size="xsmall">
				{heading}
			</Heading>
		</Box>
		{children}
	</>
);

export const SearchResultContainer = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => <Box xcss={styles.searchResultContainer}>{children}</Box>;

const styles = cssMap({
	externalLink: {
		color: token('color.text.subtle'),
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none',
		},
		'&:visited': {
			textDecoration: 'none',
		},
	},
	searchResultContainer: {
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
	},
	sectionHeading: {
		paddingTop: token('space.100'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.250'),
	},
});
